import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Task {
  id: string;
  job_id: string | null;
  title: string;
  description: string | null;
  task_type: string;
  priority: string;
  due_at: string | null;
  completed_at: string | null;
  created_at: string;
  assigned_user: { id: string; name: string; email: string } | null; // Legacy
  assignees: { id: string; name: string; email: string }[]; // Multi-assignee
  created_user: { id: string; name: string } | null;
  job: { id: string; job_number: string; leads: { address: string; homeowner_name: string } } | null;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

const PRIORITY_COLORS: Record<string, string> = {
  low: '#4A6580',
  normal: '#4A7EB5',
  high: '#F59E0B',
  urgent: '#EF4444',
};

const TASK_TYPE_ICONS: Record<string, string> = {
  follow_up: '📞',
  scheduling: '📅',
  field_work: '🔧',
  document: '📄',
  approval: '✅',
  reminder: '⏰',
};

export default function TasksPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'mine' | 'completed'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  
  // Create form state
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    task_type: 'follow_up',
    assigned_to: [] as string[], // Multi-assignee support
    due_at: '',
    priority: 'normal',
  });

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, [filter]);

  const fetchTasks = async () => {
    try {
      const endpoint = filter === 'mine' 
        ? '/tasks/mine' 
        : `/tasks?completed=${filter === 'completed' ? 'true' : 'false'}`;
      const res = await api.get(endpoint);
      setTasks(res.data);
    } catch (e) {
      console.error('Failed to fetch tasks', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (e) {
      console.error('Failed to fetch users', e);
    }
  };

  const createTask = async () => {
    if (!newTask.title || newTask.assigned_to.length === 0) {
      alert('Title and at least one assignee are required');
      return;
    }
    try {
      await api.post('/tasks', {
        ...newTask,
        due_at: newTask.due_at || null,
      });
      setShowCreateModal(false);
      setNewTask({ title: '', description: '', task_type: 'follow_up', assigned_to: [], due_at: '', priority: 'normal' });
      setAssigneeSearch('');
      setShowAssigneeDropdown(false);
      fetchTasks();
    } catch (e) {
      alert('Failed to create task');
    }
  };

  const toggleComplete = async (task: Task) => {
    try {
      if (task.completed_at) {
        await api.post(`/tasks/${task.id}/uncomplete`);
      } else {
        await api.post(`/tasks/${task.id}/complete`);
      }
      fetchTasks();
    } catch (e) {
      alert('Failed to update task');
    }
  };

  const deleteTask = async (id: string) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await api.delete(`/tasks/${id}`);
      fetchTasks();
    } catch (e) {
      alert('Failed to delete task');
    }
  };

  const formatDue = (due: string | null) => {
    if (!due) return null;
    const d = new Date(due);
    const now = new Date();
    const diff = d.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    
    if (days < 0) return { text: `${Math.abs(days)}d overdue`, color: '#EF4444' };
    if (days === 0) return { text: 'Due today', color: '#F59E0B' };
    if (days === 1) return { text: 'Due tomorrow', color: '#F59E0B' };
    if (days <= 7) return { text: `Due in ${days}d`, color: '#4A7EB5' };
    return { text: d.toLocaleDateString(), color: '#4A6580' };
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: '#fff', fontWeight: 800, fontSize: 28, margin: 0 }}>📋 Tasks</h1>
          <p style={{ color: '#4A6580', margin: '4px 0 0' }}>Team task management</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{ background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
        >
          + New Task
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { key: 'all', label: 'All Open' },
          { key: 'mine', label: 'My Tasks' },
          { key: 'completed', label: 'Completed' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key as any)}
            style={{
              background: filter === f.key ? '#4A7EB5' : '#1A2B3C',
              color: filter === f.key ? '#fff' : '#4A6580',
              border: '1px solid #2D4259',
              borderRadius: 8,
              padding: '8px 16px',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Task List */}
      {loading ? (
        <div style={{ color: '#4A6580', textAlign: 'center', padding: 40 }}>Loading...</div>
      ) : tasks.length === 0 ? (
        <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 40, textAlign: 'center', border: '1px solid #2D4259' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✨</div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>No tasks</div>
          <div style={{ color: '#4A6580', fontSize: 14 }}>
            {filter === 'completed' ? 'No completed tasks yet' : 'All caught up!'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {tasks.map(task => {
            const due = formatDue(task.due_at);
            return (
              <div
                key={task.id}
                style={{
                  background: '#1A2B3C',
                  borderRadius: 12,
                  padding: '16px 20px',
                  border: '1px solid #2D4259',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  opacity: task.completed_at ? 0.6 : 1,
                }}
              >
                {/* Checkbox */}
                <button
                  onClick={() => toggleComplete(task)}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    border: `2px solid ${task.completed_at ? '#10B981' : '#4A6580'}`,
                    background: task.completed_at ? '#10B981' : 'transparent',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {task.completed_at && '✓'}
                </button>

                {/* Icon */}
                <span style={{ fontSize: 24, flexShrink: 0 }}>{TASK_TYPE_ICONS[task.task_type] || '📋'}</span>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 15,
                      textDecoration: task.completed_at ? 'line-through' : 'none',
                    }}>
                      {task.title}
                    </span>
                    <span style={{
                      background: PRIORITY_COLORS[task.priority] + '22',
                      color: PRIORITY_COLORS[task.priority],
                      border: `1px solid ${PRIORITY_COLORS[task.priority]}44`,
                      borderRadius: 4,
                      padding: '2px 6px',
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                    }}>
                      {task.priority}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#4A6580', fontSize: 12 }}>
                    {task.assignees?.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {task.assignees.slice(0, 3).map((a, i) => (
                          <div key={a.id} style={{ width: 22, height: 22, borderRadius: 11, background: '#4A7EB5', border: '2px solid #1A2B3C', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 9, fontWeight: 700, marginLeft: i > 0 ? -8 : 0 }}>
                            {a.name.charAt(0)}
                          </div>
                        ))}
                        {task.assignees.length > 3 && (
                          <span style={{ marginLeft: 4, color: '#8FA8C0', fontSize: 11 }}>+{task.assignees.length - 3}</span>
                        )}
                        <span style={{ marginLeft: 6 }}>{task.assignees.map(a => a.name.split(' ')[0]).join(', ')}</span>
                      </div>
                    )}
                    {task.job && (
                      <span
                        onClick={() => navigate(`/pipeline/${task.job!.id}`)}
                        style={{ cursor: 'pointer', color: '#4A7EB5' }}
                      >
                        📍 {task.job.leads?.address || task.job.job_number}
                      </span>
                    )}
                    {due && (
                      <span style={{ color: due.color }}>{due.text}</span>
                    )}
                  </div>
                </div>

                {/* Delete */}
                <button
                  onClick={() => deleteTask(task.id)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#4A6580',
                    cursor: 'pointer',
                    padding: 8,
                    fontSize: 16,
                  }}
                >
                  🗑️
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 28, width: 560, maxHeight: '90vh', overflowY: 'auto', border: '1px solid #2D4259' }}>
            <h2 style={{ color: '#fff', fontWeight: 800, margin: '0 0 20px' }}>New Task</h2>
            
            {/* Quick Templates */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ color: '#4A6580', fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 8 }}>QUICK TEMPLATES</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[
                  { title: 'Call homeowner', type: 'follow_up', icon: '📞', priority: 'normal' },
                  { title: 'Schedule job', type: 'scheduling', icon: '📅', priority: 'high' },
                  { title: 'Follow up on insurance', type: 'follow_up', icon: '📧', priority: 'normal' },
                  { title: 'Order materials', type: 'field_work', icon: '📦', priority: 'high' },
                  { title: 'Get supplement approved', type: 'approval', icon: '✅', priority: 'high' },
                  { title: 'Upload missing docs', type: 'document', icon: '📄', priority: 'normal' },
                  { title: 'Check on crew progress', type: 'field_work', icon: '👷', priority: 'normal' },
                  { title: 'Collect final payment', type: 'follow_up', icon: '💵', priority: 'urgent' },
                ].map((tmpl, i) => (
                  <button
                    key={i}
                    onClick={() => setNewTask(p => ({ ...p, title: tmpl.title, task_type: tmpl.type, priority: tmpl.priority }))}
                    style={{
                      background: newTask.title === tmpl.title ? '#4A7EB5' : '#243447',
                      border: '1px solid #2D4259',
                      borderRadius: 20,
                      padding: '6px 12px',
                      color: '#fff',
                      fontSize: 12,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>{tmpl.icon}</span>
                    <span>{tmpl.title}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ color: '#4A6580', fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>TITLE *</label>
              <input
                value={newTask.title}
                onChange={e => setNewTask(p => ({ ...p, title: e.target.value }))}
                placeholder="What needs to be done?"
                style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '12px', color: '#fff', fontSize: 14 }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ color: '#4A6580', fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>DESCRIPTION</label>
              <textarea
                value={newTask.description}
                onChange={e => setNewTask(p => ({ ...p, description: e.target.value }))}
                placeholder="Additional details..."
                rows={3}
                style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '12px', color: '#fff', fontSize: 14, resize: 'none' }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ color: '#4A6580', fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>ASSIGN TO *</label>
              
              {/* Selected assignees as chips */}
              {newTask.assigned_to.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {newTask.assigned_to.map(userId => {
                    const u = users.find(x => x.id === userId);
                    if (!u) return null;
                    return (
                      <div key={userId} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#4A7EB533', border: '1px solid #4A7EB5', borderRadius: 16, padding: '4px 8px 4px 4px' }}>
                        <div style={{ width: 20, height: 20, borderRadius: 10, background: '#4A7EB5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 10 }}>
                          {u.name.charAt(0)}
                        </div>
                        <span style={{ color: '#fff', fontSize: 12, fontWeight: 500 }}>{u.name}</span>
                        <button
                          type="button"
                          onClick={() => setNewTask(p => ({ ...p, assigned_to: p.assigned_to.filter(id => id !== userId) }))}
                          style={{ background: 'none', border: 'none', color: '#8FA8C0', cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1 }}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Search dropdown */}
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={assigneeSearch}
                  onChange={e => { setAssigneeSearch(e.target.value); setShowAssigneeDropdown(true); }}
                  onFocus={() => setShowAssigneeDropdown(true)}
                  placeholder={newTask.assigned_to.length > 0 ? 'Add more people...' : 'Search team members...'}
                  style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '10px 12px', color: '#fff', fontSize: 14 }}
                />
                {showAssigneeDropdown && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#243447', border: '1px solid #4A7EB5', borderRadius: 8, marginTop: 4, maxHeight: 200, overflowY: 'auto', zIndex: 100 }}>
                    {users
                      .filter(u => !newTask.assigned_to.includes(u.id))
                      .filter(u => u.name.toLowerCase().includes(assigneeSearch.toLowerCase()))
                      .slice(0, 8)
                      .map(u => (
                        <div
                          key={u.id}
                          onClick={() => {
                            setNewTask(p => ({ ...p, assigned_to: [...p.assigned_to, u.id] }));
                            setAssigneeSearch('');
                            setShowAssigneeDropdown(false);
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #2D4259' }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#1E3448')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                          <div style={{ width: 28, height: 28, borderRadius: 14, background: '#4A7EB5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 12 }}>
                            {u.name.charAt(0)}
                          </div>
                          <div>
                            <div style={{ color: '#fff', fontWeight: 500, fontSize: 13 }}>{u.name}</div>
                            <div style={{ color: '#4A6580', fontSize: 11 }}>{u.role}</div>
                          </div>
                        </div>
                      ))}
                    {users.filter(u => !newTask.assigned_to.includes(u.id)).filter(u => u.name.toLowerCase().includes(assigneeSearch.toLowerCase())).length === 0 && (
                      <div style={{ padding: 12, color: '#4A6580', textAlign: 'center', fontSize: 13 }}>No matching team members</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ color: '#4A6580', fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>TYPE</label>
                <select
                  value={newTask.task_type}
                  onChange={e => setNewTask(p => ({ ...p, task_type: e.target.value }))}
                  style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '12px', color: '#fff', fontSize: 14 }}
                >
                  <option value="follow_up">📞 Follow-up</option>
                  <option value="scheduling">📅 Scheduling</option>
                  <option value="field_work">🔧 Field Work</option>
                  <option value="document">📄 Document</option>
                  <option value="approval">✅ Approval</option>
                  <option value="reminder">⏰ Reminder</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div>
                <label style={{ color: '#4A6580', fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>DUE DATE</label>
                <input
                  type="datetime-local"
                  value={newTask.due_at}
                  onChange={e => setNewTask(p => ({ ...p, due_at: e.target.value }))}
                  style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '12px', color: '#fff', fontSize: 14 }}
                />
              </div>
              <div>
                <label style={{ color: '#4A6580', fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>PRIORITY</label>
                <select
                  value={newTask.priority}
                  onChange={e => setNewTask(p => ({ ...p, priority: e.target.value }))}
                  style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', borderRadius: 8, padding: '12px', color: '#fff', fontSize: 14 }}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{ background: '#243447', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 24px', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={createTask}
                style={{ background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 24px', fontWeight: 700, cursor: 'pointer' }}
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
